// "Cash stamp" section (§6.1 / §6.4a — STAFF_GRANT).
//
// For a cash / non-heywaitr sale, staff grant a stamp directly. This is the
// trust-anchored exception to A5: a SCAN never mints, but an AUTHENTICATED staff
// member tapping "give stamp" does (exactly like a barista stamping a paper card),
// audited + rate-limited server-side. We POST /guest/stamp/grant {identityToken,
// campaign, qty, note}. The granting staff identity is derived from the Bearer
// token server-side (req.decoded.userid) — never sent by the client (A4).
//
// identityToken = WHICH wallet to credit (A4): the customer's short pass code
// from their wallet's "show my card to staff" QR (GET /guest/wallet/pass).
// Staff scan it with the camera (html5-qrcode via the shared QrScanView) or
// type it. The backend resolves the pass to the wallet while unexpired; a grant
// without it is rejected (wallet_identity_required) — identity is never guessed.
import React, { useState } from 'react';
import axios from 'axios';
import { API, authHeaders, describeError } from './api';
import CampaignPicker from './CampaignPicker';
import QrScanView from '../../components/QrScanView';

const MAX_QTY = 10;

export default function CashStamp({ campaigns, onAuthExpired }) {
  const [campaign, setCampaign] = useState('');
  const [pass, setPass] = useState('');
  const [scanning, setScanning] = useState(false);
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const clamp = (n) => Math.max(1, Math.min(MAX_QTY, n));

  const submit = async () => {
    if (busy) return;
    setError('');
    setSuccess('');
    if (!campaign) {
      setError('Pick a campaign first.');
      return;
    }
    if (!pass.trim()) {
      setError('Scan or type the customer’s card pass first.');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/guest/stamp/grant`,
        {
          campaign,
          qty: clamp(qty),
          note: note.trim() || undefined,
          identityToken: pass.trim(),
        },
        { headers: authHeaders() },
      );
      const data = res.data || {};
      const p = data.progress || {};
      const count = p.currentCount != null ? p.currentCount : null;
      const max = p.maxCount != null ? p.maxCount : null;
      let msg = `Granted ${clamp(qty)} stamp${clamp(qty) > 1 ? 's' : ''}.`;
      if (count != null && max != null) msg += ` Card now ${count}/${max}.`;
      else if (count != null) msg += ` Card now at ${count}.`;
      setSuccess(msg);
      setQty(1);
      setNote('');
      setPass('');
    } catch (err) {
      if (err && err.response && (err.response.status === 401 || err.response.status === 403)) {
        if (onAuthExpired) {
          onAuthExpired();
          return;
        }
      }
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="staff-card">
      <h2>Cash stamp</h2>
      <p className="staff-hint">
        Give a stamp for a cash or non-app sale. Ask the customer to open their
        wallet and show their card pass. This is logged to your staff account.
      </p>

      {error ? <div className="staff-alert is-error">{error}</div> : null}
      {success ? <div className="staff-alert is-ok">{success}</div> : null}

      {scanning ? (
        <>
          <QrScanView
            onScan={(text) => {
              setScanning(false);
              setPass(String(text || '').trim());
            }}
            hint="Point the camera at the customer’s card pass QR"
          />
          <button
            className="staff-btn staff-btn-ghost"
            type="button"
            style={{ marginTop: 10 }}
            onClick={() => setScanning(false)}
          >
            Cancel scan
          </button>
        </>
      ) : (
        <>
          <label className="staff-label" htmlFor="cash-pass">
            Customer card pass
          </label>
          <input
            id="cash-pass"
            className="staff-input"
            placeholder="Scan or type the pass code…"
            value={pass}
            onChange={(e) => setPass(e.target.value.toUpperCase())}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
          />
          <button
            className="staff-btn staff-btn-ghost"
            type="button"
            style={{ marginTop: 8 }}
            onClick={() => { setError(''); setScanning(true); }}
          >
            📷 Scan customer pass
          </button>

          <CampaignPicker
            id="cash-campaign"
            campaigns={campaigns}
            value={campaign}
            onChange={(id) => {
              setCampaign(id);
              setSuccess('');
              setError('');
            }}
          />

          <label className="staff-label">Quantity</label>
          <div className="staff-stepper">
            <button
              type="button"
              aria-label="decrease"
              onClick={() => setQty((q) => clamp(q - 1))}
            >
              −
            </button>
            <div className="staff-qty">{qty}</div>
            <button
              type="button"
              aria-label="increase"
              onClick={() => setQty((q) => clamp(q + 1))}
            >
              +
            </button>
          </div>

          <label className="staff-label" htmlFor="cash-note">
            Note (optional)
          </label>
          <textarea
            id="cash-note"
            className="staff-textarea"
            placeholder="e.g. cash sale — 2 coffees"
            value={note}
            maxLength={140}
            onChange={(e) => setNote(e.target.value)}
          />

          <button className="staff-btn" type="button" disabled={busy} onClick={submit}>
            {busy ? <span className="staff-spinner" /> : null}
            {busy ? 'Granting…' : `Give ${qty} stamp${qty > 1 ? 's' : ''}`}
          </button>
        </>
      )}
    </div>
  );
}
